import { gql } from '@apollo/client';
import { BASE_COMPANY_FRAGMENT } from './company.fragments';
import { IMAGE_VARIANT_FRAGMENT } from './media.fragments';

export const SITE_LOG_FRAGMENT = gql`
  fragment siteLogValues on SiteLog {
    id
    comment
    userEmail
    user {
      firstName
      lastName
      email
    }
    company {
      id
      name
      logo {
        ...imageVariant
      }
    }
    timestamp
    action
  }
  ${IMAGE_VARIANT_FRAGMENT}
`;

type TSitesType =
  | 'GenericSite'
  | 'Farm'
  | 'Mine'
  | 'Forest'
  | 'CollectionSite'
  | 'OriginSite'
  | 'Plant'
  | 'ProcessingSite'
  | 'Factory'
  | 'ManufacturingSite'
  | 'Warehouse'
  | 'HandlingSite'
  | 'GroceryStore'
  | 'DistributionSite'
  | 'SiteCluster';

export const BASE_DEFAULT_SITE_FRAGMENT = (siteType: TSitesType) => gql`
  fragment baseDefault${siteType}Values on ${siteType} {
    id
    ownedBy {
      ...companyBaseValues
    }
    createdBy {
      ...companyBaseValues
    }
    image {
      ...imageVariant
    }
    siteType
    title
    lang
    locationCoordinates {
      lat
      lng
    }
    siteClusterId
    modifiedTimestamp
    createdTimestamp
    locationName
    imageId
    externalId
    description
    coordinateFeatures {
      featureType
      locationName
      center {
        lat
        lng
      }
    }
    inUse
    countryCode
  }
    ${IMAGE_VARIANT_FRAGMENT}
    ${BASE_COMPANY_FRAGMENT}
  `;

export const DEFAULT_SITE_FRAGMENT = (siteType: TSitesType) => gql`
  fragment default${siteType}Values on ${siteType} {
    ...baseDefault${siteType}Values
    logs {
      ...siteLogValues
    }
  }
    ${BASE_DEFAULT_SITE_FRAGMENT(siteType)}
    ${SITE_LOG_FRAGMENT}
  `;

export const BASE_FARM_SITE_FRAGMENT = gql`
  fragment baseFarmSiteValues on Farm {
    ...baseDefaultFarmValues
    siteClusterId
    sizeHa
    numberOfFarmers
    landOwnership
    additionalRawMaterialIds
    farmOwner
    plotsCount
    plotsSizeHa
    rawMaterial {
      id
      title
    }
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('Farm')}
`;

export const FARM_SITE_FRAGMENT = gql`
  fragment farmSiteValues on Farm {
    ...defaultFarmValues
    siteClusterId
    sizeHa
    numberOfFarmers
    landOwnership
    additionalRawMaterialIds
    farmOwner
    plotsCount
    plotsSizeHa
    rawMaterial {
      id
      title
    }
  }
  ${DEFAULT_SITE_FRAGMENT('Farm')}
`;

export const BASE_GENERIC_SITE_FRAGMENT = gql`
  fragment baseGenericSiteValues on GenericSite {
    ...baseDefaultGenericSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('GenericSite')}
`;

export const GENERIC_SITE_FRAGMENT = gql`
  fragment genericSiteValues on GenericSite {
    ...defaultGenericSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('GenericSite')}
`;

export const BASE_MINE_SITE_FRAGMENT = gql`
  fragment baseMineValues on Mine {
    ...baseDefaultMineValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('Mine')}
`;

export const MINE_SITE_FRAGMENT = gql`
  fragment mineValues on Mine {
    ...defaultMineValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('Mine')}
`;

export const BASE_FOREST_SITE_FRAGMENT = gql`
  fragment baseForestValues on Forest {
    ...baseDefaultForestValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('Forest')}
`;

export const FOREST_SITE_FRAGMENT = gql`
  fragment forestValues on Forest {
    ...defaultForestValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('Forest')}
`;

export const BASE_COLLECTION_SITE_FRAGMENT = gql`
  fragment baseCollectionSiteValues on CollectionSite {
    ...baseDefaultCollectionSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('CollectionSite')}
`;

export const COLLECTION_SITE_FRAGMENT = gql`
  fragment collectionSiteValues on CollectionSite {
    ...defaultCollectionSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('CollectionSite')}
`;

export const BASE_ORIGIN_SITE_FRAGMENT = gql`
  fragment baseOriginSiteValues on OriginSite {
    ...baseDefaultOriginSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('OriginSite')}
`;

export const ORIGIN_SITE_FRAGMENT = gql`
  fragment originSiteValues on OriginSite {
    ...defaultOriginSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('OriginSite')}
`;

export const BASE_PLANT_SITE_FRAGMENT = gql`
  fragment basePlantValues on Plant {
    ...baseDefaultPlantValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('Plant')}
`;

export const PLANT_SITE_FRAGMENT = gql`
  fragment plantValues on Plant {
    ...defaultPlantValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('Plant')}
`;

export const BASE_PROCESSING_SITE_FRAGMENT = gql`
  fragment baseProcessingSiteValues on ProcessingSite {
    ...baseDefaultProcessingSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('ProcessingSite')}
`;

export const PROCESSING_SITE_FRAGMENT = gql`
  fragment processingSiteValues on ProcessingSite {
    ...defaultProcessingSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('ProcessingSite')}
`;

export const BASE_FACTORY_SITE_FRAGMENT = gql`
  fragment baseFactoryValues on Factory {
    ...baseDefaultFactoryValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('Factory')}
`;

export const FACTORY_SITE_FRAGMENT = gql`
  fragment factoryValues on Factory {
    ...defaultFactoryValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('Factory')}
`;

export const BASE_MANUFACTURING_SITE_FRAGMENT = gql`
  fragment baseManufacturingSiteValues on ManufacturingSite {
    ...baseDefaultManufacturingSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('ManufacturingSite')}
`;

export const MANUFACTURING_SITE_FRAGMENT = gql`
  fragment manufacturingSiteValues on ManufacturingSite {
    ...defaultManufacturingSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('ManufacturingSite')}
`;

export const BASE_WAREHOUSE_SITE_FRAGMENT = gql`
  fragment baseWarehouseValues on Warehouse {
    ...baseDefaultWarehouseValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('Warehouse')}
`;

export const WAREHOUSE_SITE_FRAGMENT = gql`
  fragment warehouseValues on Warehouse {
    ...defaultWarehouseValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('Warehouse')}
`;

export const BASE_HANDLING_SITE_FRAGMENT = gql`
  fragment baseHandlingSiteValues on HandlingSite {
    ...baseDefaultHandlingSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('HandlingSite')}
`;

export const HANDLING_SITE_FRAGMENT = gql`
  fragment handlingSiteValues on HandlingSite {
    ...defaultHandlingSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('HandlingSite')}
`;

export const BASE_GROCERY_STORE_SITE_FRAGMENT = gql`
  fragment baseGroceryStoreValues on GroceryStore {
    ...baseDefaultGroceryStoreValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('GroceryStore')}
`;

export const GROCERY_STORE_SITE_FRAGMENT = gql`
  fragment groceryStoreValues on GroceryStore {
    ...defaultGroceryStoreValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('GroceryStore')}
`;

export const BASE_DISTRIBUTION_SITE_FRAGMENT = gql`
  fragment baseDistributionSiteValues on DistributionSite {
    ...baseDefaultDistributionSiteValues
    siteClusterId
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('DistributionSite')}
`;

export const DISTRIBUTION_SITE_FRAGMENT = gql`
  fragment distributionSiteValues on DistributionSite {
    ...defaultDistributionSiteValues
    siteClusterId
  }
  ${DEFAULT_SITE_FRAGMENT('DistributionSite')}
`;

export const BASE_SITE_CLUSTER_FRAGMENT = gql`
  fragment baseSiteClusterValues on SiteCluster {
    ...baseDefaultSiteClusterValues
    sitesType
    sitesRawMaterial {
      id
      title
    }
    sitesCount
    plotsCount
    plotsSizeHa
  }
  ${BASE_DEFAULT_SITE_FRAGMENT('SiteCluster')}
`;

export const SITE_CLUSTER_FRAGMENT = gql`
  fragment siteClusterValues on SiteCluster {
    ...defaultSiteClusterValues
    sitesType
    sitesCount
    plotsCount
    plotsSizeHa
    sites(first: 3) {
      edges {
        node {
          ...genericSiteValues
          ...farmSiteValues
          ...mineValues
          ...forestValues
          ...originSiteValues
          ...collectionSiteValues
          ...plantValues
          ...processingSiteValues
          ...factoryValues
          ...manufacturingSiteValues
          ...warehouseValues
          ...handlingSiteValues
          ...groceryStoreValues
          ...baseDistributionSiteValues
        }
      }
    }
  }
  ${DEFAULT_SITE_FRAGMENT('SiteCluster')}
  ${GENERIC_SITE_FRAGMENT}
  ${FARM_SITE_FRAGMENT}
  ${MINE_SITE_FRAGMENT}
  ${FOREST_SITE_FRAGMENT}
  ${COLLECTION_SITE_FRAGMENT}
  ${ORIGIN_SITE_FRAGMENT}
  ${PLANT_SITE_FRAGMENT}
  ${PROCESSING_SITE_FRAGMENT}
  ${FACTORY_SITE_FRAGMENT}
  ${MANUFACTURING_SITE_FRAGMENT}
  ${WAREHOUSE_SITE_FRAGMENT}
  ${HANDLING_SITE_FRAGMENT}
  ${GROCERY_STORE_SITE_FRAGMENT}
  ${BASE_DISTRIBUTION_SITE_FRAGMENT}
`;

export const BASE_SITE_FRAGMENT = gql`
  fragment baseSiteValues on Site {
    ...baseGenericSiteValues
    ...baseFarmSiteValues
    ...baseMineValues
    ...baseForestValues
    ...baseOriginSiteValues
    ...baseCollectionSiteValues
    ...basePlantValues
    ...baseProcessingSiteValues
    ...baseFactoryValues
    ...baseManufacturingSiteValues
    ...baseWarehouseValues
    ...baseHandlingSiteValues
    ...baseGroceryStoreValues
    ...baseDistributionSiteValues
    ...baseSiteClusterValues
    __typename
  }
  ${BASE_GENERIC_SITE_FRAGMENT}
  ${BASE_FARM_SITE_FRAGMENT}
  ${BASE_MINE_SITE_FRAGMENT}
  ${BASE_FOREST_SITE_FRAGMENT}
  ${BASE_COLLECTION_SITE_FRAGMENT}
  ${BASE_ORIGIN_SITE_FRAGMENT}
  ${BASE_PLANT_SITE_FRAGMENT}
  ${BASE_PROCESSING_SITE_FRAGMENT}
  ${BASE_FACTORY_SITE_FRAGMENT}
  ${BASE_MANUFACTURING_SITE_FRAGMENT}
  ${BASE_WAREHOUSE_SITE_FRAGMENT}
  ${BASE_HANDLING_SITE_FRAGMENT}
  ${BASE_GROCERY_STORE_SITE_FRAGMENT}
  ${BASE_DISTRIBUTION_SITE_FRAGMENT}
  ${BASE_SITE_CLUSTER_FRAGMENT}
`;

export const SITE_FRAGMENT = gql`
  fragment siteValues on Site {
    ...genericSiteValues
    ...farmSiteValues
    ...mineValues
    ...forestValues
    ...originSiteValues
    ...collectionSiteValues
    ...plantValues
    ...processingSiteValues
    ...factoryValues
    ...manufacturingSiteValues
    ...warehouseValues
    ...handlingSiteValues
    ...groceryStoreValues
    ...distributionSiteValues
    ...siteClusterValues
    __typename
  }
  ${GENERIC_SITE_FRAGMENT}
  ${FARM_SITE_FRAGMENT}
  ${MINE_SITE_FRAGMENT}
  ${FOREST_SITE_FRAGMENT}
  ${COLLECTION_SITE_FRAGMENT}
  ${ORIGIN_SITE_FRAGMENT}
  ${PLANT_SITE_FRAGMENT}
  ${PROCESSING_SITE_FRAGMENT}
  ${FACTORY_SITE_FRAGMENT}
  ${MANUFACTURING_SITE_FRAGMENT}
  ${WAREHOUSE_SITE_FRAGMENT}
  ${HANDLING_SITE_FRAGMENT}
  ${GROCERY_STORE_SITE_FRAGMENT}
  ${DISTRIBUTION_SITE_FRAGMENT}
  ${SITE_CLUSTER_FRAGMENT}
`;
